import { Carousel } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC } from 'react';

import { AuthEntryPoint } from '../../features/auth/types';
import { useIsSubscribed } from '../../hooks/subscription';
import { useAuthenticate } from '../../hooks/useAuthenticate';
import { AugmentedArtistCard } from '../../types/Artists';
import { GeneratedSubscriptionType } from '../../types/generated';
import { getArtistTileOverline } from '../../utils/artists';
import { ArtistProfileCard } from '../ArtistProfileCard/ArtistProfileCard';

interface Props {
  artists: AugmentedArtistCard[];
  onClick: (artist: AugmentedArtistCard) => void;
  onSlide: (index: number) => void;
  title: string;
}

export const GlossaryArtists: FC<Props> = ({
  artists,
  onClick,
  onSlide,
  title,
}) => {
  const { t } = useTranslation(['artists', 'authentication']);
  const { authenticateAndSubscribe } = useAuthenticate();

  const subscriptions = artists.map(({ internalId = '' }) => ({
    objectId: internalId,
    type: GeneratedSubscriptionType.Artist,
  }));

  const { checkIsSubscribed, refetch } = useIsSubscribed(subscriptions);

  if (artists.length === 0) {
    return null;
  }

  return (
    <Carousel
      headline={title}
      items={artists.map((artist) => {
        const subscription = {
          objectId: artist.internalId ?? '',
          type: GeneratedSubscriptionType.Artist,
          name: artist.name,
          ref: '',
        };

        return (
          <ArtistProfileCard
            key={artist.name}
            href={`/${artist.link}`}
            image={{
              alt: artist.name,
              focus: artist.focus || undefined,
              src: artist.image,
            }}
            isSubscribed={checkIsSubscribed(
              artist.internalId,
              GeneratedSubscriptionType.Artist,
            )}
            name={artist.name}
            onClick={() => onClick(artist)}
            onClickSubscribe={async () => {
              await authenticateAndSubscribe({
                authScreenThumbnail: artist.image,
                authScreenTitle: t('authentication:dynamicTitle.artist'),
                subscriptions: [subscription],
                analyticsProperties: {
                  source: AuthEntryPoint.Artist,
                  'button_location': 'glossary term page',
                  'artist name': artist.name,
                },
              });
              refetch();
            }}
            overline={
              artist.hasUpcomingRelease ? t('artistUpcomingRelease') : undefined
            }
            subtitle={getArtistTileOverline(
              artist.totalWorks,
              artist.hideCollaborations,
              t,
            )}
          />
        );
      })}
      onSlide={onSlide}
    />
  );
};
